import { useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { heroReveal } from "@/lib/motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { HeroBookingWidget } from "@/components/HeroBookingWidget";
import { useLanguage } from "@/context/LanguageContext";
import { useSeo } from "@/lib/useSeo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO_PAGES } from "@/data/seoPages";
import { Check, ArrowRight } from "lucide-react";

export default function SeoLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const page = SEO_PAGES[slug];
  const c = page ? page[lang] || page.en : null;

  useSeo(c ? {
    title: c.metaTitle, description: c.metaDesc,
    path: `/services/${slug}`, image: page?.image, lang,
  } : {});

  useEffect(() => {
    if (!c) return;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-seo-jsonld", "true");
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: c.faq.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);
    return () => {
      document.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
    };
  }, [c, slug]);

  if (!page) return <Navigate to="/" replace />;

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center">
        <div className="absolute inset-0 overflow-hidden">
          <img src={page.image} alt={c.h1} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/72 to-black/55" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-16 w-full grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs tracking-luxe uppercase text-white/60 mb-5">{c.eyebrow}</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.03] text-white">{c.h1}</h1>
            <p className="text-white/70 text-base sm:text-lg max-w-xl mt-6 leading-relaxed">{c.intro}</p>
            <ul className="mt-8 space-y-3">
              {c.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/80 text-sm">
                  <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-white" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <motion.div {...heroReveal}>
            <HeroBookingWidget />
          </motion.div>
        </div>
      </section>

      {/* Use cases */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="font-serif font-light text-4xl sm:text-5xl text-white mb-12 leading-tight">{c.useCasesTitle}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {c.useCases.map((u) => (
            <div key={u} className="rounded-xl border border-white/10 bg-card px-6 py-5 text-white/85 flex items-center gap-3">
              <span className="h-2 w-2 rounded-full bg-white flex-shrink-0" /> {u}
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-24">
        <h2 className="font-serif font-light text-4xl text-white mb-12 text-center leading-tight">{t("faq.title")}</h2>
        <Accordion type="single" collapsible className="w-full">
          {c.faq.map((f, i) => (
            <AccordionItem key={f.q} value={`q${i}`} className="border-white/10">
              <AccordionTrigger className="text-white text-left hover:no-underline text-base">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-card/30 py-20">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-white leading-tight">{t("cta.title")}</h2>
          <Button data-testid="seo-cta-btn" className="rounded-full bg-white text-black hover:bg-white/85 h-14 px-10 text-base mt-8"
            onClick={() => navigate(`/book?service=${page.service}`)}>
            {t("widget.submit")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
